import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import { format } from 'date-fns'

import DisplayOrdersTable from '../../../User/Profile/UserOrders/DisplayOrdersTable';
import RefundDisplay from '../../../Pay/Refund/RefundOrderDisplay';
//import Refund from '../../../Pay/Refund';
import Wizard from '../../../Pay/Refund/Wizard';
//import SimpleRefund from '../../../Pay/Refund/SimpleRefund';
import MobileRefundDisplay from '../../../Pay/Refund/RefundOrderDisplay/MobileRefundDisplay';
import MobileDisplayOrdersTable from '../../../User/Profile/UserOrders/DisplayOrdersTable/MobileDisplayOrdersTable';
import { authUserHasModuleAccessMany } from '../../../../utils/auth';
import { loadCompanyConfig } from '../../../../utils/thunks';
import TableSkeleton from '../../../../components/common/TableSkeleton';

import Toast from '../../../../components/Toast';

import APIPOS from '../../../../api/Pos';
import {flattenErrorMessage, fromCents, hasClawbackEligibleTokens, isTokenOptionsPayload, toCents} from '../../../Pay/Refund/refundUtils';
//import styles from './OrderItems.module.scss';

const REFUND_PERMISSION_MODULE_ID = 178;
const VIEW_ADVANCED_ORDER_DETAILS_MODULE_ID =298;

/** Component that simply displays the orders from a transaction. 
 * @param object currentOrder -the order that is being displayed
 * @param function props.handleCheckbBox  -what happens when an order is checked
 * @requires order_status_id===2(Completed)||order_status_id===5(partially_refunded) for checkboxes to be enabled.  Being 1(Pending), 3(Cancelled), or 4(Refunded) will disbale checkboxes
 */
const OrderItems = ({
    currentOrder=null, 
    parentOrder,
    modifiedRefund, 
    modifiedCompleted, 
    setError, 
    setSuccess, 
    refunded, 
    partialRefund,
    doneStatuses,
    redrawOrder,
    registerId,
    loading=false
}) => {

    const mountedRef = useRef(false);
    const tokenPreviewRequestRef = useRef(0);
    const containerRef = useRef(null);
    const dispatch = useDispatch();
    const companyConfig = useSelector(state => state.company?.config);

    const [ isMobile, setIsMobile ]=useState(false);
    const [ isAdmin, setIsAdmin ]=useState(false);
    const [ isPatron, setIsPatron ]=useState(false);
    const [ refundSuccess, setRefundSuccess ]=useState(false);
    const [ orderLength, setOrderLength ]=useState(0)
    const [ refundTotal, setRefundTotal ]=useState(0);
    const [ refundItems, setRefundItems ]=useState([]);
    const [ showAdvancedRefund, setShowAdvancedRefund ]=useState(false);
    const [ refundable, setRefundable ]=useState(null);
    const [ disableRefunds, setDisableRefunds]=useState(true);
    //const [ simpleRefund, setSimpleRefund ]=useState(false);
    const [ itemsRefundable, setItemsRefundable ]=useState([]);
    const [ refundRegister, setRefundRegister ]=useState(null);
    const [ refundTokenPreview, setRefundTokenPreview ]=useState({data: null, error: null, loading: false});
    const hasTokens = refundTokenPreview.data?.has_tokens === true;

    const statusCheck=useCallback(()=>{
        const refundableArray = [12, 2, 8, 9, 10, 11];
        return refundableArray.includes(+currentOrder.order_status_id);
    },[currentOrder])

    const getAllRefundable=useCallback(()=>{
        if(currentOrder){
           let refundableItems=[]
           if (hasTokens) {
               if(mountedRef.current) setItemsRefundable(refundableItems);
               return;
           }
           currentOrder.items.forEach((item)=>{
               if (item.total_refunded_amount === 0 && item.shipping_refunded_amount === 0 && item.product_type_id!==9){
                   refundableItems.push(item);
               }
           })
           if(mountedRef.current) setItemsRefundable(refundableItems);
        }
   },[currentOrder, hasTokens]);

   //Determines if the whole transaction is refundable or not
    const determineRefundability=useCallback(()=>{
        let bool=false;
        const isRefundable = statusCheck();
        if(isRefundable) bool = true;
        if (currentOrder.admin_fee_refunded !== 0) bool = false; //if admin fee has been refunded
        currentOrder.items.forEach((item)=>{ //if any part of an item has been refunded
            if (item.total_refunded_amount !== 0 //already had some for items refunded
                || item.shipping_refunded_amount !==0) //already had shipping refunded
            bool = false;
        })
        if (currentOrder.split_order_ids?.length > 0) bool = false; //if it's a parent order
        if (currentOrder.items.length === 0 && !currentOrder.parent_id) bool = false; //if there are no items - the exception being if it's a child order
        if (mountedRef.current ===true) setRefundable(bool);
    },[currentOrder, statusCheck]);

    const loadRefundTokenOptions=useCallback(async()=>{
        if (!isAdmin || !currentOrder?.id) return;

        const requestId = ++tokenPreviewRequestRef.current;
        setRefundTokenPreview({data: null, error: null, loading: true});
        try {
            const response = await APIPOS.payment.getRefundTokenOptions({order_id: currentOrder.id});
            if (response?.errors || response?.error || !isTokenOptionsPayload(response?.data)) throw response;
            if (mountedRef.current && tokenPreviewRequestRef.current === requestId) {
                setRefundTokenPreview({data: response.data, error: null, loading: false});
            }
        } catch (error) {
            if (mountedRef.current && tokenPreviewRequestRef.current === requestId) {
                setRefundTokenPreview({
                    data: null,
                    error: flattenErrorMessage(error) || "Token refund options could not be verified.",
                    loading: false,
                });
            }
        }
    },[currentOrder?.id, isAdmin]);

    const addTotalRefund=useCallback((item, checked)=>{

        /*let shipping = 0, tax = 0;
        let value = currentOrder.admin_fee_total; // ??? why is this here?
        if (item.shipping) shipping = item.shipping;
        if (item.tax) tax = item.tax;
        value = +item.price + +shipping + +tax;
        let num = Math.round(value*100)/100;
        setRefundTotal(prev => +prev + checked ? +num : -num);*/

        //we need to remove any admin authorized discounts from the total.  Those didn't have any cash or card value, so refunding them will just lose the company money
        let total = toCents(currentOrder.payment_total);
        const managerPayments = currentOrder?.transactions?.filter((transaction)=>transaction?.transaction_payment_method_id === 5 ) || [];
        if(managerPayments?.length > 0){
            managerPayments.forEach((payment)=>{
                total -= toCents(payment.amount);
            })
        }
        //we have to remove the change given as cash from the total, too
        const cashPayments = currentOrder?.transactions?.filter((transaction)=>transaction?.transaction_payment_method_id === 2)
        if(cashPayments?.length > 0){
            cashPayments.forEach(payment=>{
                total -= toCents(payment.change);
            })
        }

        setRefundTotal(fromCents(Math.max(0, total)));

    },[currentOrder]);

    //Disable/enable refund button based on having items selected
    useEffect(()=>{
        const isRefundable = statusCheck();
        const canRefundFinancially = toCents(refundTotal) > 0;
        const canStartOrdinaryRefund = refundable && isRefundable && canRefundFinancially;
        const canMediateTokensOnly = toCents(currentOrder.payment_total) === 0
            && hasClawbackEligibleTokens(refundTokenPreview.data);
        if (canStartOrdinaryRefund || canMediateTokensOnly) setDisableRefunds(false);
        else setDisableRefunds(true);
    }, [refundTotal, refundable, refundTokenPreview.data, currentOrder.payment_total, currentOrder.order_status_id,statusCheck]);

    //Set mounted on mount and unmount
    useEffect(()=>{
        mountedRef.current =true;

        const checkModulePermissions=async()=>{
            try{
                let response = await authUserHasModuleAccessMany([REFUND_PERMISSION_MODULE_ID, VIEW_ADVANCED_ORDER_DETAILS_MODULE_ID]);
                if (response){
                    setIsAdmin(response[REFUND_PERMISSION_MODULE_ID])
                    setIsPatron(!response[VIEW_ADVANCED_ORDER_DETAILS_MODULE_ID])
                } else { //if there's a problem, assume the user doesn't have access
                    setIsAdmin(false);
                    setIsPatron(true);
                }
            } catch(ex) {
                console.error(ex);
            }
        }

        const getCompanyConfig=async()=>{
            if (companyConfig) setRefundRegister(companyConfig.refund_register);
            else {
                if (currentOrder) setRefundRegister(currentOrder?.register_id);
                dispatch(loadCompanyConfig());
            }
        }

        getCompanyConfig();
        checkModulePermissions();
        if (containerRef.current.offsetWidth < 540) setIsMobile(true);
        else setIsMobile(false);

        return()=>{
            mountedRef.current = false;
        }
    },[companyConfig, dispatch, currentOrder]);

    useEffect(()=>{
        loadRefundTokenOptions();
    },[loadRefundTokenOptions]);

    //Handles changing between refund and order display
    useEffect(()=>{
        if(showAdvancedRefund === false && refundItems.length > 0){
            for(let i = 0; i < refundItems.length; i++){
               const checkbox = document.getElementById(refundItems[i].key)
               checkbox.checked = true;
            }
        }
    }, [showAdvancedRefund,refundItems]);

    //this makes sure we have the order refundability before assigning refundability to items
    useEffect(()=>{
        if(refundable || refundable === false){ //have to specify false here to make sure it isn't null anymore
            const noRefundCriteria=(item, addon=false)=>{
                let disabled = false;
                let refundableStatus = statusCheck();
                if((!refundableStatus) //for orders, complete is 2, partially paid is 12, and there's no transactionId
                        || ((item.total_refund_amount && item?.total_refunded_amount !== 0) //already refunded
                        || (item.shipping_refund_amount && item?.shipping_refunded_amount !==0)  //already refunded
                        || (!addon && item?.product_type_id===9)  //token product
                        || hasTokens) //token-bearing orders cannot use itemized refunds
                    ){
                        //If the order is any status other than complete, it disables the item.  Item is also disabled if there is any refunded amount in either field.
                        //Also disables items individually based on having a refund amount or being a service token (type 9).
                        disabled = true; 
                    }
                    if(refundable === false) disabled = true //if the whole order is not refundable, an item cannot be refundable
                    item.disabled = disabled;

            }

            currentOrder.items.forEach(item => {
                noRefundCriteria(item);
                if (item?.addons?.length > 0) {
                    item.addons.forEach(addon => {
                        noRefundCriteria(addon, true);
                    });
                }
            })

            if (refundable) addTotalRefund(null, true);
            else setRefundTotal(0);

        } 
    },[refundable, currentOrder, addTotalRefund, statusCheck, hasTokens]);

    //This determines refundability.  In the future, when partial refunds are available, add other order_status_id (such as refunded), and allow the individual item disabling to handle it.
    useEffect(()=>{

        if(currentOrder){
            determineRefundability();
            getAllRefundable();
            setOrderLength(currentOrder.items.length)
            
        }
    },[currentOrder, getAllRefundable, determineRefundability]);

    
    /**Adds items names/prices for the items to be refunded */
    const recordRefundedItems=(item, checked)=>{
        if(checked === true){
            setRefundItems(refundItems => [ ...refundItems, item])
        }
        if(checked === false){
            for(let i =0; i <refundItems.length; i++){
                if(refundItems[i].name === item.name){
                    refundItems.splice(i, 1)
                    break;
                }
            }
        }
    }
    
    const handleCheckBox=(item, e, i)=>{
        let checked = e.target.checked
        let price = (item.final_price *item.quantity) 
        let key = `cb-items-${item.id}`
        let info = {item_id: item.id, name: item.product_name, price: price.toFixed(2), key: key, shipping: item.shipping, id:item.id, tax: item.tax}
        addTotalRefund(info, checked);
        recordRefundedItems(info, checked);
    }

    const cleanUp=()=>{
        setShowAdvancedRefund(false);
        //setSimpleRefund(false);
        redrawOrder();
        loadRefundTokenOptions();
    }

    
    const tokenDisplay=(token)=>{
        if(!token)return
        else{
            let tokenDetails = token[0]
            let tokenTotal = token?.length || 0
            let expiredAmount=token?.filter(each =>each.status === "expired")?.length || 0
            let refundableAmount=token?.filter(each=>each.can_claw_back)?.length || 0
            let consumedAmount = token?.filter(each=>each.consumed_at)?.length || 0
            return(
                <>
                    <tr>
                        <th>
                            {" "}
                        </th>
                        <th>
                            {" "}
                        </th>
                        <th>
                            {" "}
                        </th>
                        {/* Make some gaps to make it line up nicely and indicate that there's a seperation between table items */}
                        <th>
                            Token Details
                        </th>
                        <th>
                            {tokenTotal === 1 ?
                                "Status"
                                :
                                `${tokenTotal} bundled tokens`
                            }
                        </th>
                            <th>
                            Refundable
                        </th>
                    
                        <th>
                            Consumed
                        </th>
                    
                        <th>
                            Expired
                        </th>
                    </tr>
                    <tr>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td>
                            {tokenTotal <= 1 ?
                                <span>
                                    {token?.status}
                                </span>
                                :
                                ""
                            }
                        </td>
                        <td>
                            {tokenTotal <= 1 ? 
                                <>
                                    {token?.can_claw_back ? <>Yes</>:<>No</>}
                                </>
                                :
                                <>
                                    {refundableAmount}/{tokenTotal}
                                </>
                            }
                        </td>
                        <td>
                            {tokenTotal <= 1 ?
                                <>{token?.consumed_at ?<>{format(new Date(token?.consumed_at), "MM/dd/yy")}</>:<>---</>}</>
                                :
                                <>
                                    {consumedAmount}/{tokenTotal}
                                </>
                            }
                        </td>
                        <td>
                            {tokenTotal <= 1 ? 
                                <>{token?.expires_at ? <>{format(new Date(token?.expires_at), "MM/dd/yy")}</>:<>---</>}</>
                                :
                                <>
                                    {expiredAmount}/{tokenTotal}
                                </>
                            }
                        </td>
                    </tr>
                </> 
            )
        }
    }

    const tokenDisplayMobile=(token)=>{
        if(!token) return;
        else{
            let tokenDetails = token[0]
            let tokenTotal = token?.length || 0
            let expiredAmount=token?.filter(each =>each.status === "expired")?.length || 0
            let refundableAmount=token?.filter(each=>each.can_claw_back)?.length || 0
            let consumedAmount = token?.filter(each=>each.consumed_at)?.length || 0
            return(
                <>
                    {tokenTotal <= 1 && 
                        <tr>
                            <th>Token Status</th>
                            <td>{token?.status}</td>
                        </tr>
                    }
                    <tr>
                        <th>Refundable</th>
                        <td>
                            {tokenTotal <= 1 ? 
                                <>
                                    {token?.can_claw_back ? <>Yes</>:<>No</>}
                                </>
                                :
                                <>
                                    {refundableAmount}/{tokenTotal}
                                </>
                            }
                        </td>
                    </tr>
                    <tr>
                        <th>Consumed</th>
                        <td>
                            {tokenTotal <= 1 ?
                                <>{token?.consumed_at ?<>{format(new Date(token?.consumed_at), "MM/dd/yy")}</>:<>---</>}</>
                                :
                                <>
                                    {consumedAmount}/{tokenTotal}
                                </>
                            }
                        </td>
                    </tr>
                    <tr>
                        <th>
                            Expired
                        </th>
                        <td>
                            {tokenTotal <= 1 ? 
                                <>{token?.expires_at ? <>{format(new Date(token?.expires_at), "MM/dd/yy")}</>:<>---</>}</>
                                :
                                <>
                                    {expiredAmount}/{tokenTotal}
                                </>
                            }
                        </td>
                    </tr>
                </>
            )
        }
    }

    return (
        <div ref={containerRef}>
            {loading ?
                <table>
                    <tbody>
                        <TableSkeleton />
                    </tbody>
                </table>
                :
                <>
                    {currentOrder&& currentOrder?.payment_method_id!==7 && !currentOrder.parent_id &&
                        <>{partialRefund ?
                            <div> 
                                <h5>
                                    <i className="far fa-tag" />
                                    Refunded Items
                                </h5> 
                                {isMobile ?
                                    <MobileRefundDisplay 
                                        tokenDisplay={tokenDisplayMobile} 
                                        tokenData={refundTokenPreview} 
                                        currentOrder={modifiedRefund} 
                                        isPatron={isPatron} 
                                    />
                                :
                                    <RefundDisplay 
                                        tokenDisplay={tokenDisplay} 
                                        tokenData={refundTokenPreview} 
                                        currentOrder={modifiedRefund} 
                                        isPatron={isPatron} 
                                    />
                                }
                                <h5>
                                    <i className="far fa-tags" />
                                    Order Items
                                </h5>
                                {isMobile ? 
                                    <MobileDisplayOrdersTable 
                                        tokenDisplay={tokenDisplayMobile} 
                                        tokenData={refundTokenPreview} 
                                        currentOrder={modifiedCompleted} 
                                        handleCheckBox={handleCheckBox} 
                                        isAdmin={isAdmin} 
                                        isPatron={isPatron} 
                                    />
                                :
                                    <DisplayOrdersTable 
                                        tokenDisplay={tokenDisplay} 
                                        tokenData={refundTokenPreview} 
                                        currentOrder={modifiedCompleted} 
                                        handleCheckBox={handleCheckBox} 
                                        isAdmin={isAdmin} 
                                        isPatron={isPatron}
                                    />
                                }
                            </div>
                        :
                            <>
                                {refunded ?
                                    <>
                                        <h5>
                                            <i className="far fa-tag" />
                                            Refunded Items
                                        </h5> 
                                        {isMobile ? 
                                            <MobileRefundDisplay
                                                tokenDisplay={tokenDisplayMobile}  
                                                tokenData={refundTokenPreview} 
                                                currentOrder={currentOrder} 
                                                isPatron={isPatron} 
                                            />
                                            :
                                            <RefundDisplay 
                                                tokenDisplay={tokenDisplay} 
                                                tokenData={refundTokenPreview} 
                                                currentOrder={currentOrder} 
                                                isPatron={isPatron}
                                            />
                                        }
                                    </>
                                :
                                    <>
                                        <h5>
                                            <i className="far fa-tags" />
                                            Order Items
                                        </h5> 
                                        {isMobile ? 
                                            <MobileDisplayOrdersTable 
                                                tokenDisplay={tokenDisplayMobile} 
                                                tokenData={refundTokenPreview} 
                                                handleCheckBox={handleCheckBox} 
                                                isAdmin={(currentOrder.split_order_ids && currentOrder.split_order_ids.length > 0) ? false : isAdmin} 
                                                currentOrder={currentOrder} 
                                                isPatron={isPatron} 
                                            />
                                        :
                                            <DisplayOrdersTable
                                                tokenDisplay={tokenDisplay}  
                                                tokenData={refundTokenPreview} 
                                                handleCheckBox={handleCheckBox} 
                                                isAdmin={(currentOrder.split_order_ids && currentOrder.split_order_ids.length > 0) ? false : isAdmin} 
                                                currentOrder={currentOrder} 
                                                isPatron={isPatron} 
                                            />
                                        }
                                    </>
                                }
                            </>
                        }</>
                    }   
                    {currentOrder?.payment_method_id===7 && //Token redemptions
                        <span className="d-flex justify-content-center mb-5 bold">
                            This transaction was a token redemption.
                        </span>
                    }
                    {currentOrder.parent_id && //child of a split payment
                        <>
                            {parentOrder &&
                                <>
                                    {isMobile ? 
                                        //these are false for isAdmin because isAdmin determines if refunds can be made.  Split payments are by amount and as such, can only use a simple refund
                                        <MobileDisplayOrdersTable handleCheckBox={handleCheckBox} isAdmin={false} currentOrder={parentOrder} isPatron={isPatron} />
                                    :
                                        <DisplayOrdersTable handleCheckBox={handleCheckBox} isAdmin={false} currentOrder={parentOrder} isPatron={isPatron} />
                                    }
                                </>
                            }
                        </>
                    }
                    {isPatron || !hasTokens ?
                        null :
                        <p className="small my-3">
                            **Token products and token-bearing bundles cannot be selected as itemized returns.**
                            <br />
                            Use the "Refund" button to choose the financial amount and how to handle any tokens from this purchase.
                        </p>
                    }
                    {!isPatron && refundTokenPreview.error && toCents(refundTotal) === 0 &&
                        <p className="small my-3 text-danger" data-cy="refund-token-preview-error">
                            Token-only refund options could not be verified. {refundTokenPreview.error}{' '}
                            <Button type="button" variant="link" className="p-0 align-baseline" onClick={loadRefundTokenOptions}>
                                Try again
                            </Button>
                        </p>
                    }
                    {!isPatron && refundTokenPreview.loading && toCents(refundTotal) === 0 &&
                        <p className="small my-3" data-cy="refund-token-preview-loading">Checking token-only refund eligibility...</p>
                    }
                </>    
            }
            {currentOrder && isAdmin && 
                /*currentOrder.order_status_id === 2 && currentOrder.payment_method_id !==7 &&*/ !currentOrder.split_order_ids.length &&
                    <>
                        <Button  
                            data-cy="advanced-refund-btn"
                            variant="primary"
                            disabled={currentOrder?.split_order_ids?.length > 0 || disableRefunds}
                            onClick={()=>setShowAdvancedRefund(!showAdvancedRefund)}>
                            {/*Goes back to the detail page when an advanced refund is active */}
                            {showAdvancedRefund ? ". . ." : "Refund"}
                        </Button> 
                        {refundSuccess && 
                            <div className="py-1 success-text">{refundSuccess}</div>
                        }
                        {/*!showAdvancedRefund &&
                            <Button variant="primary" data-cy="refund-all-btn" disabled={!refundable} onClick={()=>setSimpleRefund(true)}>
                                Refund All
                            </Button>
                        */}
                    </>
                }
                {/*simpleRefund &&
                    <Modal show={simpleRefund} onHide={()=>setSimpleRefund(false)} className={styles.modal} backdropClassName={styles.backdrop}>
                        <Modal.Header closeButton/>
                        <Modal.Body>
                            <SimpleRefund 
                                fullOrder={currentOrder}
                                orderId={currentOrder.id}
                                userId={currentOrder.user_id}
                                user={`${currentOrder.user_fname} ${currentOrder.user_lname}`}
                                registerId={refundRegister} 
                                items={itemsRefundable}
                                onClose={()=>setSimpleRefund(false)}
                                adminFee={currentOrder.admin_fee_total}
                                setError={setError}
                                setSuccess={setSuccess}
                                cleanUp={cleanUp}
                            />
                        </Modal.Body>
                    </Modal>
                */}
                {showAdvancedRefund &&
                    <>
                        <Wizard
                            order={currentOrder}
                            paymentRegister={refundRegister}
                            maxTotal={refundTotal} 
                            setError={setError}
                            setSuccess={setSuccess}
                            cleanUp={cleanUp}
                            onHide={(success)=>{
                                setShowAdvancedRefund(false);
                                if(success) {
                                    cleanUp();
                                    setSuccess(<Toast>Refund successful!</Toast>);
                                    setRefundSuccess("Refund successful!");
                                }
                            }}
                            redrawOrder={redrawOrder}
                            registerId={registerId}
                        />

                        {/*
                        <Modal show={showAdvancedRefund} size={"xl"} onHide={()=>setShowAdvancedRefund(false)} className={styles.modal} backdropClassName={styles.backdrop}>
                            <Modal.Header closeButton/>
                            <Modal.Body>
                                <Refund 
                                    fullOrder={currentOrder}
                                    paymentRegister={refundRegister}
                                    maxTotal={refundTotal} 
                                    transaction={currentOrder.transactions[0]}
                                    adminFee={currentOrder.admin_fee_total} 
                                    payment_method={currentOrder.payment_method.toLowerCase()}
                                    items={refundItems} 
                                    setError={setError}
                                    setSuccess={setSuccess}
                                    cleanUp={cleanUp}
                                    orderLength={orderLength}
                                    amountRefund={currentOrder.parent_id ? true : false}
                                    transactionId={currentOrder?.transactions[0]?.transaction_type_id !==3 ? currentOrder?.transactions[0]?.id : 0} //if the order has yet to be refunded, there will only be one transaction, but if it's refunded, set to 0 as there is no 0 transaction
                                /> 
                                
                            </Modal.Body>
                        </Modal>
                    */}
                    </>
                }
        </div>
    )
}

export default OrderItems;
